import { create } from "zustand";
import {
    defaultExtent,
    infra3dStartPosition,
    EPSG
} from "./data";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import Point from "@arcgis/core/geometry/Point";
import { DefaultExtent } from "../../interfaces/defaultExtentInterface";
import { IAppLayer, IAppLayers } from '../../interfaces/AppLayerInterface'


interface MapStore {
    isMapTopicExpanded: boolean;
    toggleMapTopic: () => void;
    isMapLegendExpanded: boolean;
    toggleMapLegend: () => void;
    mapTopics: IAppLayers[];
    setMapTopic: (mapTopics: IAppLayers[]) => void;
    selectMapTopic: (id: number) => void;
    layers: IAppLayer[];
    allLayers: IAppLayer[];
    selectedLayerInfra3d: IAppLayer | null;
    toggleLayer: (id: number | null, title?: string) => void;
    infraPosition: Point;
    displayInfraPosition: boolean;
    extent: DefaultExtent;
    infraOrientation: number;
    infra3dInitialized: boolean;
    setInfra3dInitialized: (initialized: boolean) => void;
}

const useMapStore = create<MapStore>((set, get) => ({
    isMapTopicExpanded: false,
    toggleMapTopic: () => {
        const isExpanded = get().isMapTopicExpanded;
        set(() => ({ isMapTopicExpanded: !isExpanded }));
    },
    isMapLegendExpanded: false,
    toggleMapLegend: () => {
        const isExpanded = get().isMapLegendExpanded;
        set(() => ({ isMapLegendExpanded: !isExpanded }));
    },
    mapTopics: [],
    setMapTopic: (mapTopics) => {
        const activeTopic = mapTopics.find((topic) => topic.active) ?? mapTopics[0];
        const activeLayers = activeTopic?.layers ?? [];
        const selectedLayer = activeLayers.find((layer) => layer.selected) ?? activeLayers[0] ?? null;

        set(() => ({
            mapTopics: mapTopics,
            allLayers: mapTopics.flatMap((topic) => topic.layers),
            layers: activeLayers,
            selectedLayerInfra3d: selectedLayer
        }));
    },
    selectMapTopic: (id) => {
        const result = get().mapTopics.map((topic) => ({
            ...topic,
            active: topic.id === id,
            layers: topic.layers.map((layer, index) => ({
                ...layer,
                selected: topic.id === id ? index === 0 : false,
            }))
        }));

        const activeTopic = result.find((mt) => mt.active);

        if (!activeTopic) {
            return;
        }

        const selectedLayer = activeTopic.layers.find((layer) => layer.selected) ?? null;

        set(() => ({
            selectedLayerInfra3d: selectedLayer,
            layers: activeTopic.layers,
            mapTopics: result
        }));
    },
    layers: [],
    allLayers: [],
    selectedLayerInfra3d: null,
    toggleLayer: (id: number | null, title?: string) => {
        const currentLayers = get().layers;

        const clickedLayer = currentLayers.find(
            (layer) => layer.layerId === id && layer.title === title
        );

        if (!clickedLayer) {
            return;
        }

        const shouldSelectClickedLayer = !clickedLayer.selected;

        const updatedLayers = currentLayers.map((layer) => ({
            ...layer,
            selected:
                shouldSelectClickedLayer &&
                layer.layerId === id &&
                layer.title === title,
        }));

        const selectedLayer = shouldSelectClickedLayer
            ? updatedLayers.find(
            (layer) => layer.layerId === id && layer.title === title
        ) ?? null
            : null;

        const updatedMapTopics = get().mapTopics.map((topic) => {
            if (!topic.active) {
                return topic;
            }

            return {
                ...topic,
                layers: updatedLayers,
            };
        });

        set(() => ({
            layers: updatedLayers,
            mapTopics: updatedMapTopics,
            selectedLayerInfra3d: selectedLayer,
        }));
    },
    infraPosition: new Point({
        x: infra3dStartPosition[0],
        y: infra3dStartPosition[1],
        spatialReference: new SpatialReference({ wkid: EPSG }),
    }), // Coordinate actually shown on the map
    displayInfraPosition: false,
    extent: defaultExtent,
    infraOrientation: 0,
    infra3dInitialized: false,
    setInfra3dInitialized: (initialized) => {
        set(() => ({ infra3dInitialized: initialized }));
    },
}));

export default useMapStore;
